import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink, useNavigate } from "react-router-dom";
// @mui
import { Icon } from "@iconify/react";
import { Backdrop, Box, Button, CircularProgress, Divider, Fade, Link, MenuItem, Paper, Portal, Skeleton, Stack, Typography } from "@mui/material";
import Iconify from "src/components/iconify";
import { alpha } from "@mui/material/styles";

// routes
// auth
// Redux selectors - single source of truth for auth state
import {
  selectCurrentRole,
  selectUser,
  selectIsAuthenticated,
  selectAvailableRoles,
} from "src/redux/authSlice";
// components
import Label from "src/components/label";

import * as Sentry from "@sentry/react";
import { logoutAppUser } from "src/api/megs-api-api";
import { getSubDistributorGroup } from "src/api/sub-distributor-groups-api";
import { setRoleSwitchInProgress } from "src/api/axiosAuth";
import logger from "src/utils/logger";
import { useSnackbar } from "../../../components/snackbar";
import MenuPopover from "../../../components/menu-popover";
import { CustomAvatar } from "../../../components/custom-avatar";
import { IconButtonAnimate } from "../../../components/animate";
import { useAuthContext } from "../../../auth/useAuthContext";
import { PATH_AUTH, PATH_DASHBOARD } from "../../../routes/paths";

export default function AccountPopover() {
  const navigate = useNavigate();

  const { logout, fetchAvailableRoles, switchRole } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();

  // Use Redux selectors - single source of truth for auth state
  const selectedRole = useSelector(selectCurrentRole);
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const reduxAvailableRoles = useSelector(selectAvailableRoles);

  const [openPopover, setOpenPopover] = useState(null);
  const [groupsMap, setGroupsMap] = useState({});
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [rolesError, setRolesError] = useState(null);
  const [isSwitchingRole, setIsSwitchingRole] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Handle unauthenticated state - redirect to login
  useEffect(() => {
    if (!isAuthenticated || !selectedRole) {
      if (!isRedirecting) {
        setIsRedirecting(true);
        navigate(PATH_AUTH.loginAdmin, { replace: true });
      }
    }
  }, [isAuthenticated, selectedRole, isRedirecting, navigate]);

  // Fetch available roles when popover opens
  // Always fetch fresh data to catch server-side role changes (admin add/remove)
  // Redux is used for immediate display while fetch is in progress
  useEffect(() => {
    let isMounted = true;

    async function loadRoles() {
      if (!openPopover || !fetchAvailableRoles) {
        return;
      }

      // Only show loading spinner if Redux has no cached roles
      // Otherwise show cached data immediately while fetching fresh data
      if (reduxAvailableRoles.length === 0) {
        setIsLoadingRoles(true);
      }

      try {
        setRolesError(null);
        const roles = await fetchAvailableRoles();

        if (!isMounted) {
          return;
        }

        // fetchAvailableRoles updates Redux directly - just log if empty
        if (!roles || roles.length === 0) {
          logger.info("No roles returned from API");
        }
      } catch (error) {
        logger.error("Failed to fetch available roles", error);
        Sentry.captureException(error);
        if (isMounted) {
          setRolesError("Failed to load roles");
        }
      } finally {
        if (isMounted) {
          setIsLoadingRoles(false);
        }
      }
    }

    loadRoles();

    return () => {
      isMounted = false;
    };
  }, [openPopover, fetchAvailableRoles, reduxAvailableRoles.length]);

  // Fetch group details for group_manager roles (only from server-validated roles)
  useEffect(() => {
    let isMounted = true;

    async function fetchAllGroups() {
      const groupManagerRoles = reduxAvailableRoles?.filter((role) => role.role === "group_manager" && role.group_id) || [];
      const newGroupsMap = {};

      // Fetch in parallel for better performance
      const fetchPromises = groupManagerRoles.map(async (role) => {
        try {
          const response = await getSubDistributorGroup(role.group_id);
          if (response.status_code === 1001) {
            return { groupId: role.group_id, data: response.data };
          }
        } catch (error) {
          logger.error("Error fetching group", error, { groupId: role.group_id });
        }
        return null;
      });

      const results = await Promise.all(fetchPromises);
      results.forEach((result) => {
        if (result) {
          newGroupsMap[result.groupId] = result.data;
        }
      });

      if (isMounted) {
        setGroupsMap(newGroupsMap);
      }
    }

    if (openPopover && reduxAvailableRoles.length > 0) {
      fetchAllGroups();
    }

    return () => {
      isMounted = false;
    };
  }, [openPopover, reduxAvailableRoles]);

  // Don't render if not authenticated (redirect handled by useEffect above)
  if (!isAuthenticated || !selectedRole) {
    return null;
  }

  // Use Redux as single source of truth for roles
  const rolesToDisplay = reduxAvailableRoles;
  // sort roles: admin first, then alphabetically by customer_name
  const sortedUserRoles = [...rolesToDisplay].sort((a, b) => {
    if (a.role === "admin") {
      return -1; // a should come before b
    } if (b.role === "admin") {
      return 1; // b should come before a
    } 
      // Null-safe comparison - roles without customer_name sort to end
      const aName = a.customer_name || "";
      const bName = b.customer_name || "";
      return aName.toLowerCase().localeCompare(bName.toLowerCase());
    
  });

  const handleOpenPopover = (event) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleLogout = async () => {
    try {
      const logoutUser = await logoutAppUser();

      if (logoutUser.status_code === 1001) {
        logout();
        navigate(PATH_AUTH.loginAdmin, { replace: true });
        handleClosePopover();
      } else {
        logger.error("Logout failed", null, { response: logoutUser.data });
        enqueueSnackbar("Unable to logout!", { variant: "error" });
      }
    } catch (error) {
      logger.error("Logout error", error);
      enqueueSnackbar("Unable to logout!", { variant: "error" });
    }
  };

  const handleClickItem = async (role) => {
    // Require server-side role switch - no client-side fallback (security)
    if (!switchRole) {
      logger.error("Role switch function unavailable", null, { roleType: role.role });
      enqueueSnackbar("Role switching is currently unavailable", { variant: "error" });
      return;
    }

    if (!role.role_id) {
      // Log security event for roles without role_id (should not happen with new backend)
      const warningContext = {
        roleType: role.role,
        customerId: role.customer_id,
      };
      logger.error("Cannot switch to role without role_id", null, warningContext);
      Sentry.captureMessage("Role switch blocked - missing role_id", {
        level: "warning",
        extra: warningContext,
      });
      enqueueSnackbar("This role cannot be switched to. Please contact support.", { variant: "error" });
      return;
    }

    // Close popover and show loading state
    handleClosePopover();
    setIsSwitchingRole(true);

    try {
      const success = await switchRole(role.role_id);
      if (success) {
        // Navigate away from old page
        navigate("/", { replace: true });

        // Clear role switch flag and loading state after navigation completes
        // During this window, any 401s from old page's stale requests are suppressed
        setTimeout(() => {
          setRoleSwitchInProgress(false);
          setIsSwitchingRole(false);
        }, 500);

        enqueueSnackbar("Role switched successfully", { variant: "success", autoHideDuration: 3000 });
      } else {
        enqueueSnackbar("Failed to switch role", { variant: "error" });
        setIsSwitchingRole(false);
      }
    } catch (error) {
      logger.error("Role switch error", error, { roleId: role.role_id });
      Sentry.captureException(error);
      enqueueSnackbar("An error occurred while switching roles", { variant: "error" });
      setIsSwitchingRole(false);
    }
  };

  // Get current group for display if user is in group_manager role
  const currentGroup = selectedRole?.role === "group_manager" && selectedRole?.group_id
    ? groupsMap[selectedRole.group_id]
    : null;

  return (
    <>
      {/* Loading overlay during role switch - Portal escapes AppBar stacking context */}
      <Portal>
        <Backdrop
          sx={{
            zIndex: (theme) => theme.zIndex.modal + 1,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
          }}
          open={isSwitchingRole}
        >
          <Paper
            elevation={24}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 280,
              px: 4,
              py: 5,
              borderRadius: 2,
            }}
          >
            <CircularProgress
              size={48}
              thickness={4}
              sx={{ color: "primary.main", mb: 3 }}
            />
            <Typography
              variant="h6"
              sx={{
                color: "text.primary",
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              Switching role...
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mt: 1,
                textAlign: "center",
              }}
            >
              Please wait while we update your permissions
            </Typography>
          </Paper>
        </Backdrop>
      </Portal>

      <IconButtonAnimate
        onClick={handleOpenPopover}
        sx={{
          p: 0,
          ...(openPopover && {
            "&:before": {
              zIndex: 1,
              content: "''",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              position: "absolute",
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <CustomAvatar alt={user?.displayName || "Username"} name={user?.displayName}>
          <Icon icon="eva:person-fill" />
        </CustomAvatar>
      </IconButtonAnimate>

      <MenuPopover open={openPopover} onClose={handleClosePopover} sx={{ minWidth: 250, p: 0, overflowY: "auto" }}>
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Link component={RouterLink} to={PATH_DASHBOARD.user.account} underline="none" color="inherit">
            <Typography variant="subtitle2" noWrap>
              {selectedRole?.customer_name}
            </Typography>

            <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
              {user?.username}
            </Typography>

            {currentGroup && (
              <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                {currentGroup.name}
              </Typography>
            )}
          </Link>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Stack sx={{ p: 1, minHeight: 48 }}>
          {rolesError ? (
            // Show error state when roles failed to load
            <Box sx={{ py: 2, px: 2, textAlign: "center" }}>
              <Typography variant="body2" color="error">
                {rolesError}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Please try again or contact support
              </Typography>
            </Box>
          ) : isLoadingRoles ? (
            // Show skeleton placeholders while loading
            [...Array(3)].map((_, index) => (
              <React.Fragment key={`skeleton-${index}`}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.75, px: 2 }}>
                  <Skeleton variant="text" width={120} height={24} />
                  <Skeleton variant="rounded" width={80} height={22} sx={{ ml: 1, borderRadius: 1 }} />
                </Box>
                <Divider sx={{ borderStyle: "dashed" }} />
              </React.Fragment>
            ))
          ) : (
            <Fade in={!isLoadingRoles} timeout={300}>
              <Box>
                {sortedUserRoles.map((role) => {
                  const roleKey =
                    role.role_id ||
                    role.group_id ||
                    role.customer_id ||
                    role.customer_name ||
                    role.username ||
                    role.role;
                  const roleGroup = role?.group_id ? groupsMap[role.group_id] : null;
                  
                  // Check if this role is the currently selected role
                  const isCurrentRole = selectedRole?.role_id 
                    ? selectedRole.role_id === role.role_id
                    : selectedRole?.role === role.role && 
                      selectedRole?.customer_id === role.customer_id &&
                      selectedRole?.group_id === role.group_id;

                  return (
                    <React.Fragment key={roleKey}>
                      <MenuItem 
                        onClick={() => !isCurrentRole && handleClickItem(role)} 
                        disabled={isCurrentRole}
                        sx={{ 
                          justifyContent: "space-between", 
                          my: 0, 
                          py: 0,
                          ...(isCurrentRole && {
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                            cursor: "default",
                            "&:hover": {
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                            },
                          }),
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                          {/* customer_name if there is one else username */}
                          <Typography variant="body2" noWrap sx={{ fontWeight: isCurrentRole ? 600 : 500 }}>
                            {role?.customer_name || user.username}
                          </Typography>
                          {isCurrentRole && (
                            <Iconify 
                              icon="eva:checkmark-circle-2-fill" 
                              sx={{ 
                                color: "primary.main", 
                                width: 18, 
                                height: 18,
                                flexShrink: 0,
                              }} 
                            />
                          )}
                        </Box>
                        {role?.customer_name && (
                          <Label
                            variant="soft"
                            color={
                              (role?.role === "admin" && "primary") ||
                              (role?.role === "distributor" && "secondary") ||
                              (role?.role === "sub-distributor" && "success") ||
                              (role?.role === "support_agent" && "success") ||
                              (role?.role === "finance" && "success") ||
                              (role?.role === "sub_distributor_manager" && "success") ||
                              (role?.role === "sub_distributor_operations" && "success") ||
                              (role?.role === "sub_distributor_finance" && "success") ||
                              (role?.role === "iot" && "info") ||
                              (role?.role === "agent" && "warning") ||
                              (role?.role === "group_manager" && "info") ||
                              "info"
                            }
                            sx={{ ml: 1 }}
                          >
                            {role?.role === "support_agent"
                              ? "Support Agent"
                              : role?.role === "finance"
                                ? "Finance"
                                : role?.role === "sub_distributor_manager"
                                  ? "Sub Distributor Manager"
                                  : role?.role === "sub_distributor_operations"
                                    ? "Sub Distributor Operations"
                                    : role?.role === "sub_distributor_finance"
                                      ? "Sub Distributor Finance"
                                      : role?.role === "group_manager"
                                        ? roleGroup
                                          ? `Group Manager - ${roleGroup.name}`
                                          : "Group Manager"
                                        : (role?.role === "sub-distributor" || role?.role === "agent") && role?.sub_distributor_name !== null
                                          ? `${role?.role} - ${role?.sub_distributor_name}`
                                          : role?.role}
                          </Label>
                        )}
                      </MenuItem>
                      <Divider sx={{ borderStyle: "dashed" }} />
                    </React.Fragment>
                  );
                })}
              </Box>
            </Fade>
          )}
        </Stack>

        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mx: 1, mb: 1, flexWrap: "nowrap" }}>
          <MenuItem component={RouterLink} to={PATH_DASHBOARD.user.account} onClick={handleClosePopover}>
            Change Password
          </MenuItem>

          <Button variant="contained" color="error" size="small" onClick={handleLogout} sx={{ whiteSpace: "nowrap", mx: 1 }}>
            Logout
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
